import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function BecomeSupplier() {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      // Get reCAPTCHA token if available
      let token = null;
      if (executeRecaptcha) {
        token = await executeRecaptcha('supplier_form');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const payload = { ...data };
      if (token) {
        payload.captchaToken = token;
      }
      await axios.post(`${apiUrl}/api/supplier`, payload);
      setStatus({
        type: "success",
        message: t("contact.supplier_success") || "Application submitted successfully!"
      });
      reset();
    } catch (error) {
      setStatus({
        type: "error",
        message: t("contact.supplier_error") || "Failed to submit application. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir={dir} className="min-h-screen bg-[#fafcf0] flex items-center justify-center py-16 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-12 relative"
      >
        <h2 className="text-3xl font-black text-center mb-4 text-[#2d2d2b]">
          {t("supplier_title")}
        </h2>
        <p className="text-center text-lg text-[#2d2d2b] opacity-80 mb-8">
          {t("supplier_message")}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Name */}
          <div>
            <label className="block mb-2 font-bold text-[#2d2d2b]">{t("company_name")}</label>
            <input
              type="text"
              {...register("companyName", {
                required: true,
                pattern: /^[a-zA-Z\u0600-\u06FF\s]+$/ // letters (latin + arabic) and spaces
              })}
              className={`w-full px-4 py-3 rounded-xl border text-lg focus:outline-none ${errors.companyName ? "border-red-500" : "border-gray-300 focus:ring-2 focus:ring-[#ffde21]"
                }`}
            />
            {errors.companyName && (
              <p className="text-red-500 mt-1 text-sm">
                {t("company_name_invalid") || "Please enter a valid company name (no special characters)."}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block mb-2 font-bold text-[#2d2d2b]">{t("email")}</label>
            <input
              type="email"
              {...register("email", { required: true })}
              className={`w-full px-4 py-3 rounded-xl border text-lg focus:outline-none ${errors.email ? "border-red-500" : "border-gray-300 focus:ring-2 focus:ring-[#ffde21]"
                }`}
            />
            {errors.email && (
              <p className="text-red-500 mt-1 text-sm">
                {t("email_invalid") || "Please enter a valid email address."}
              </p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block mb-2 font-bold text-[#2d2d2b]">{t("phone_number")}</label>
            <input
              type="tel"
              {...register("phone", {
                required: true,
                pattern: /^[0-9]+$/ // only numbers
              })}
              className={`w-full px-4 py-3 rounded-xl border text-lg focus:outline-none ${errors.phone ? "border-red-500" : "border-gray-300 focus:ring-2 focus:ring-[#ffde21]"
                }`}
            />
            {errors.phone && (
              <p className="text-red-500 mt-1 text-sm">
                {t("phone_invalid") || "Please enter a valid phone number (numbers only)."}
              </p>
            )}
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.03 }}
            whileTap={{ scale: loading ? 1 : 0.97 }}
            className={`w-full py-3 rounded-xl font-bold text-lg text-white transition-all duration-300 ${loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#2d2d2b] hover:bg-[#1a1a18]"
              }`}
          >
            {loading ? t("submitting") || "Submitting..." : t("submit")}
          </motion.button>

          {/* Status Message */}
          {status.message && (
            <p
              className={`text-center mt-4 font-medium ${status.type === "success" ? "text-green-600" : "text-red-600"
                }`}
            >
              {status.message}
            </p>
          )}
        </form>
      </motion.div>
    </div>
  );
}
